/**
 * Medivigil - Rule Engine
 * Matches medications and trauma levels to risk rules
 * 
 * DETERMINISTIC: Same input always produces same output
 * NO AI/LLM: Pure rule-based matching
 */

import drugClassification from '../data/drugClassification.json';
import bleedingRules from '../rules/bleedingRules.json';
import mronjRules from '../rules/mronjRules.json';
import infectionRules from '../rules/infectionRules.json';
import healingRules from '../rules/healingRules.json';
import anestheticRules from '../rules/anestheticRules.json';

// Severity levels in order (for escalation)
export const SEVERITY_ORDER = ['GREEN', 'YELLOW', 'ORANGE', 'RED'];

// All rules indexed by domain
const RULES_BY_DOMAIN = {
  bleeding: bleedingRules.rules,
  mronj: mronjRules.rules,
  infection: infectionRules.rules,
  healing: healingRules.rules,
  anesthetic: anestheticRules.rules
};

/**
 * Gets the drug classification for a normalized medication
 * @param {string} normalizedMedication - Generic medication name
 * @returns {object|null} - Classification data or null if not found
 */
export function getDrugClassification(normalizedMedication) {
  if (!normalizedMedication) return null;
  
  const classification = drugClassification.classifications[normalizedMedication.toLowerCase()];
  return classification || null;
}

/**
 * Finds matching rules for a medication category and trauma level
 * @param {string} medCategory - Medication category (e.g., 'DOAC', 'VKA')
 * @param {string} traumaLevel - Trauma level ('T1', 'T2', 'T3')
 * @param {string} riskDomain - Optional specific domain to search
 * @returns {object[]} - Matching rules
 */
export function findMatchingRules(medCategory, traumaLevel, riskDomain = null) {
  const matchingRules = [];
  
  const domainsToSearch = riskDomain 
    ? [riskDomain] 
    : Object.keys(RULES_BY_DOMAIN);
  
  for (const domain of domainsToSearch) {
    const rules = RULES_BY_DOMAIN[domain] || [];
    
    for (const rule of rules) {
      if (rule.medCategory === medCategory && rule.traumaLevel === traumaLevel) {
        matchingRules.push({
          ...rule,
          domain: domain
        });
      }
    }
  }
  
  return matchingRules;
}

/**
 * Evaluates a single medication for all applicable risks
 * @param {string} normalizedMedication - Generic medication name
 * @param {string} traumaLevel - Trauma level ('T1', 'T2', 'T3')
 * @returns {object} - Evaluation result with rules by domain
 */
export function evaluateMedication(normalizedMedication, traumaLevel) {
  const classification = getDrugClassification(normalizedMedication);
  
  if (!classification) {
    return {
      medication: normalizedMedication,
      traumaLevel,
      status: 'UNCLASSIFIED',
      message: 'Medication not found in classification database. Manual review required.',
      rulesByDomain: {}
    };
  }
  
  const rulesByDomain = {};
  
  // Find rules for each risk domain this medication affects
  for (const domain of classification.riskDomains) {
    const rules = findMatchingRules(classification.medCategory, traumaLevel, domain);
    if (rules.length > 0) {
      rulesByDomain[domain] = rules[0]; // Take first matching rule
    }
  }
  
  return {
    medication: normalizedMedication,
    traumaLevel,
    status: 'EVALUATED',
    classification: classification,
    rulesByDomain
  };
}

/**
 * Compares severity levels
 * @returns {number} - negative if a < b, 0 if equal, positive if a > b
 */
export function compareSeverity(a, b) {
  return SEVERITY_ORDER.indexOf(a) - SEVERITY_ORDER.indexOf(b);
}

/**
 * Gets the higher severity level
 */
export function getHigherSeverity(a, b) {
  return compareSeverity(a, b) >= 0 ? a : b;
}

/**
 * Escalates severity by one level (for modifiers)
 * @param {string} currentSeverity - Current severity level
 * @returns {string} - Escalated severity (max RED)
 */
export function escalateSeverity(currentSeverity) {
  const currentIndex = SEVERITY_ORDER.indexOf(currentSeverity);
  if (currentIndex === -1 || currentIndex >= SEVERITY_ORDER.length - 1) {
    return 'RED';
  }
  return SEVERITY_ORDER[currentIndex + 1];
}

/**
 * Gets rule metadata
 */
export function getRuleMetadata() {
  return {
    bleedingRules: {
      version: bleedingRules.version,
      lastUpdated: bleedingRules.lastUpdated,
      ruleCount: bleedingRules.rules.length
    },
    mronjRules: {
      version: mronjRules.version,
      lastUpdated: mronjRules.lastUpdated,
      ruleCount: mronjRules.rules.length
    },
    infectionRules: {
      version: infectionRules.version,
      lastUpdated: infectionRules.lastUpdated,
      ruleCount: infectionRules.rules.length
    },
    healingRules: {
      version: healingRules.version,
      lastUpdated: healingRules.lastUpdated,
      ruleCount: healingRules.rules.length
    },
    anestheticRules: {
      version: anestheticRules.version,
      lastUpdated: anestheticRules.lastUpdated,
      ruleCount: anestheticRules.rules.length
    },
    drugClassification: {
      version: drugClassification.version,
      lastUpdated: drugClassification.lastUpdated,
      drugCount: Object.keys(drugClassification.classifications).length
    }
  };
}

export default {
  getDrugClassification,
  findMatchingRules,
  evaluateMedication,
  compareSeverity,
  getHigherSeverity,
  escalateSeverity,
  getRuleMetadata,
  SEVERITY_ORDER
};
